import React, { useState, useEffect } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { Clock, User, CheckCircle, XCircle, AlertCircle, Search, Filter } from 'lucide-react';

const AccessRequests = () => {
  const { authState } = useOktaAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');

  const fallbackRequests = [
    {
      id: 'fallback-2',
      employeeName: 'Sarah Johnson',
      department: 'Marketing',
      role: 'Marketing Manager',
      requestType: 'Role Change',
      currentRole: 'Intern',
      requestedRole: 'UI Dev Phoenix',
      requestedAccess: 'Onboarding UI Dev',
      status: 'Approved',
      priority: 'Medium',
      requestDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(), // 5 days ago
      approvedBy: 'Deep Adhikari',
      approvedDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago
    },
    {
      id: 'fallback-3',
      employeeName: 'Mike Chen',
      department: 'IT',
      role: 'System Administrator',
      requestType: 'Team Change',
      currentTeam: 'UVS',
      requestedTeam: 'Phoenix',
      requestedAccess: '',
      status: 'Pending',
      priority: 'Low',
      requestDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(), // 1 day ago
    },
    {
      id: 'fallback-5',
      employeeName: 'Alex Rodriguez',
      department: 'IT',
      role: 'Intern',
      requestType: 'New Hire Access',
      requestedAccess: 'Basic system access for new employee onboarding',
      status: 'Approved',
      priority: 'High',
      requestDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago
      approvedBy: 'Deep Adhikari',
      approvedDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago
    }
  ];

  useEffect(() => {
    fetchAccessRequests();
  }, []);

  const fetchAccessRequests = async () => {
    try {
      const response = await fetch('/api/access-requests');
      if (!response.ok) {
        throw new Error('Failed to fetch access requests');
      }
      const data = await response.json();
      
      // If API returns empty data or fails, use fallback data
      if (!data || data.length === 0) {
        console.log('No data from API, using fallback data');
        setRequests(fallbackRequests);
      } else {
        setRequests(data);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching access requests:', error);
      console.log('API failed, using fallback data');
      // Use fallback data when API fails
      setRequests(fallbackRequests);
      setError(null); // Don't show error since we have fallback data
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    try {
      const response = await fetch(`/api/access-requests/${requestId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          status: 'Approved',
          approvedBy: authState?.idToken?.claims?.name || 'Manager',
          approvedDate: new Date().toISOString()
        }),
      });

      if (response.ok) {
        setRequests(prev => prev.map(req => 
          req.id === requestId 
            ? { ...req, status: 'Approved', approvedBy: authState?.idToken?.claims?.name || 'Manager', approvedDate: new Date().toISOString() }
            : req
        ));
      } else {
        console.error('Failed to approve request');
      }
    } catch (error) {
      console.error('Error approving request:', error);
    }
  };

  const handleReject = async (requestId) => {
    try {
      const response = await fetch(`/api/access-requests/${requestId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          status: 'Rejected',
          approvedBy: authState?.idToken?.claims?.name || 'Manager',
          approvedDate: new Date().toISOString()
        }),
      });

      if (response.ok) {
        setRequests(prev => prev.map(req => 
          req.id === requestId 
            ? { ...req, status: 'Rejected', approvedBy: authState?.idToken?.claims?.name || 'Manager', approvedDate: new Date().toISOString() }
            : req
        ));
      } else {
        console.error('Failed to reject request');
      }
    } catch (error) {
      console.error('Error rejecting request:', error);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Approved':
        return <CheckCircle size={16} className="status-icon approved" />;
      case 'Rejected':
        return <XCircle size={16} className="status-icon rejected" />;
      case 'Pending':
        return <Clock size={16} className="status-icon pending" />;
      default:
        return <AlertCircle size={16} className="status-icon" />;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'Approved':
        return 'status-badge status-approved';
      case 'Rejected':
        return 'status-badge status-rejected';
      case 'Pending':
        return 'status-badge status-pending';
      default:
        return 'status-badge';
    }
  };

  const getPriorityClass = (priority) => {
    switch (priority) {
      case 'High':
        return 'priority-badge priority-high';
      case 'Medium':
        return 'priority-badge priority-medium';
      case 'Low':
        return 'priority-badge priority-low';
      default:
        return 'priority-badge';
    }
  };

  const filteredRequests = requests.filter(request => {
    const matchesSearch = !searchQuery ||
      request.employeeName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.requestType.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.department.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.requestedAccess.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === 'All' || request.status === statusFilter;
    const matchesType = typeFilter === 'All' || request.requestType === typeFilter;

    return matchesSearch && matchesStatus && matchesType;
  });

  const requestTypes = [...new Set(requests.map(req => req.requestType))];

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <div className="loading-spinner"></div>
        <div>Loading access requests...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: '#fee',
        border: '1px solid #fcc',
        borderRadius: '8px',
        padding: '20px',
        margin: '20px 0',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <AlertCircle size={24} color="#d63031" />
        <div>
          <h3 style={{ margin: '0 0 8px 0', color: '#d63031' }}>Error Loading Requests</h3>
          <p style={{ margin: 0, color: '#666' }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="access-requests-container">
      <div className="header">
        <div className="header-text">
          <h1>Access Requests</h1>
          <p>Review and approve employee access requests and role changes</p>
        </div>
        <div className="header-stats">
          <div className="stat-card">
            <div className="stat-number">{requests.filter(r => r.status === 'Pending').length}</div>
            <div className="stat-label">Pending</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{requests.filter(r => r.status === 'Approved').length}</div>
            <div className="stat-label">Approved</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{requests.filter(r => r.status === 'Rejected').length}</div>
            <div className="stat-label">Rejected</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="search-filters" style={{ marginBottom: '24px' }}>
        <div className="search-container">
          <Search className="search-icon" />
          <input
            type="text"
            placeholder="Search by employee name, request type, or department..."
            className="search-input"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
        <select 
          className="filter-select" 
          value={statusFilter} 
          onChange={e => setStatusFilter(e.target.value)}
        >
          <option value="All">All Status</option>
          <option value="Pending">Pending</option>
          <option value="Approved">Approved</option>
          <option value="Rejected">Rejected</option>
        </select>
        <select 
          className="filter-select" 
          value={typeFilter} 
          onChange={e => setTypeFilter(e.target.value)}
        >
          <option value="All">All Types</option>
          {requestTypes.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </div>

      {/* Requests List */}
      <div className="requests-list">
        {filteredRequests.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: '#666'
          }}>
            <Clock size={64} style={{ opacity: 0.3, marginBottom: '16px' }} />
            <h3 style={{ margin: '0 0 8px 0', color: '#333' }}>No Access Requests Found</h3>
            <p style={{ margin: 0 }}>
              {searchQuery || statusFilter !== 'All' || typeFilter !== 'All' 
                ? 'No requests match your current filters.' 
                : 'No access requests have been submitted yet.'}
            </p>
          </div>
        ) : (
          filteredRequests.map(request => (
            <div key={request.id} className="request-card">
              <div className="request-header">
                <div className="request-info">
                  <div className="employee-avatar">
                    {request.employeeName.charAt(0).toUpperCase()}
                  </div>
                  <div className="request-details">
                    <h3>{request.employeeName}</h3>
                    <p>{request.department} • {request.role || request.requestedRole}</p>
                    <div className="request-meta">
                      <span className="request-type">{request.requestType}</span>
                      <span className="request-date">
                        {new Date(request.requestDate).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="request-status">
                  {getStatusIcon(request.status)}
                  <span className={getStatusClass(request.status)}>{request.status}</span>
                </div>
              </div>

              <div className="request-content">
                <div className="request-description">
                  <h4>Requested Access:</h4>
                  <p>{request.requestedAccess}</p>
                  
                  {request.requestType === 'Role Change' && (
                    <div className="change-details">
                      <p><strong>Current Role:</strong> {request.currentRole}</p>
                      <p><strong>Requested Role:</strong> {request.requestedRole}</p>
                    </div>
                  )}
                  
                  {request.requestType === 'Team Change' && (
                    <div className="change-details">
                      <p><strong>Current Team:</strong> {request.currentTeam}</p>
                      <p><strong>Requested Team:</strong> {request.requestedTeam}</p>
                    </div>
                  )}
                </div>

                {request.status === 'Pending' && (
                  <div className="request-actions">
                    <button 
                      className="approve-btn"
                      onClick={() => handleApprove(request.id)}
                    >
                      <CheckCircle size={16} />
                      Approve
                    </button>
                    <button 
                      className="reject-btn"
                      onClick={() => handleReject(request.id)}
                    >
                      <XCircle size={16} />
                      Reject
                    </button>
                  </div>
                )}

                {request.status !== 'Pending' && request.approvedBy && (
                  <div className="approval-info">
                    <User size={14} />
                    <span>
                      {request.status} by {request.approvedBy} on{' '}
                      {new Date(request.approvedDate).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AccessRequests;
